import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  CloseRounded,
  SearchRounded,
  CheckRounded,
  CollectionsRounded,
  CloudUploadRounded,
  ContentCopyRounded,
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { ImageUploader } from './ImageUploader';
import { formatFileSize } from './types';
import type { MediaImage } from './types';
import { useTranslation } from 'react-i18next';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ImagePickerDialogProps {
  open: boolean;
  tenantId: string;
  /** Called when the user confirms a selection. Receives the image URL. */
  onSelect: (url: string) => void;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ImagePickerDialog({ open, tenantId, onSelect, onClose }: ImagePickerDialogProps) {
  const { t } = useTranslation('sis');
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MediaImage | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // ── Load library ───────────────────────────────────────────────────────────

  const loadImages = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, `tenants/${tenantId}/mediaLibrary`),
          where('deletedAt', '==', null),
          orderBy('uploadedAt', 'desc'),
        ),
      );
      setImages(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data['name'] ?? '',
            url: data['url'] ?? '',
            storagePath: data['storagePath'] ?? '',
            size: data['size'] ?? 0,
            mimeType: data['mimeType'] ?? '',
            uploadedAt: data['uploadedAt']?.toDate() ?? new Date(),
            uploadedBy: data['uploadedBy'] ?? '',
          } satisfies MediaImage;
        }),
      );
    } catch (e) {
      console.error('Error loading media library', e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open) {
      loadImages();
      setSelected(null);
      setSearch('');
    }
  }, [open, loadImages]);

  // ── Filter ─────────────────────────────────────────────────────────────────

  const filtered = images.filter(
    (img) => search.trim() === '' || img.name.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Copy URL ───────────────────────────────────────────────────────────────

  function copyUrl(img: MediaImage) {
    navigator.clipboard.writeText(img.url);
    setCopiedId(img.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  // ── Handle upload complete ─────────────────────────────────────────────────

  function handleUploaded(img: MediaImage) {
    setImages((prev) => [img, ...prev]);
    setSelected(img);
    setTab('library');
  }

  // ── Confirm selection ──────────────────────────────────────────────────────

  function handleConfirm() {
    if (selected) {
      onSelect(selected.url);
      onClose();
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '80vh' } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CollectionsRounded color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {t('crm.media.title')}
            </Typography>
          </Stack>
          <IconButton aria-label={t('crm.media.close')} onClick={onClose} size="small">
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      {/* Tabs */}
      <Box sx={{ px: 3, pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab
            value="library"
            label={t('crm.media.library')}
            icon={<CollectionsRounded fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            value="upload"
            label={t('crm.media.uploadOne')}
            icon={<CloudUploadRounded fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <DialogContent
        sx={{ pt: 2, pb: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {/* ── LIBRARY TAB ── */}
        {tab === 'library' && (
          <>
            {/* Search */}
            <TextField
              size="small"
              placeholder={t('crm.media.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />

            {/* Grid */}
            {loading ? (
              <Box
                sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <CircularProgress />
              </Box>
            ) : filtered.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CollectionsRounded sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography color="text.secondary">
                  {search ? t('crm.media.noResults') : t('crm.media.emptyShort')}
                </Typography>
                {!search && (
                  <Button startIcon={<CloudUploadRounded />} onClick={() => setTab('upload')}>
                    {t('crm.media.uploadOne')}
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <ImageList cols={4} gap={8} sx={{ m: 0 }}>
                  {filtered.map((img) => {
                    const isSelected = selected?.id === img.id;
                    return (
                      <ImageListItem
                        key={img.id}
                        onClick={() => setSelected(isSelected ? null : img)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.light' },
                          position: 'relative',
                        }}
                      >
                        {/* Selection overlay */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              zIndex: 2,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckRounded sx={{ fontSize: 16, color: 'white' }} />
                          </Box>
                        )}
                        <img
                          src={img.url}
                          alt={img.name}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: 110,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <ImageListItemBar
                          title={img.name}
                          subtitle={formatFileSize(img.size)}
                          actionIcon={
                            <Tooltip title={t('crm.media.copyUrl')}>
                              <IconButton
                                aria-label={t('crm.media.copyUrl')}
                                size="small"
                                sx={{ color: 'white' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyUrl(img);
                                }}
                              >
                                {copiedId === img.id ? (
                                  <CheckRounded fontSize="small" />
                                ) : (
                                  <ContentCopyRounded fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          }
                          sx={{
                            '.MuiImageListItemBar-title': { fontSize: 12 },
                            '.MuiImageListItemBar-subtitle': { fontSize: 10 },
                          }}
                        />
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              </Box>
            )}

            {/* Selection info */}
            {selected && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
              >
                <Chip
                  label={selected.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ maxWidth: 200 }}
                />
                <Typography variant="caption" color="text.secondary" flex={1}>
                  {formatFileSize(selected.size)} — {selected.mimeType}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyRounded fontSize="small" />}
                  onClick={() => copyUrl(selected)}
                >
                  {copiedId === selected.id ? t('crm.media.copied') : t('crm.media.copyUrl')}
                </Button>
              </Stack>
            )}
          </>
        )}

        {/* ── UPLOAD TAB ── */}
        {tab === 'upload' && (
          <Box sx={{ flex: 1 }}>
            <ImageUploader tenantId={tenantId} onUploaded={handleUploaded} />
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {t('crm.media.inLibrary', { count: images.length })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            {t('crm.media.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!selected}
            sx={{ borderRadius: 2 }}
          >
            {t('crm.media.useImage')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
